import { BattleState, Monster, TurnCommands, TurnResult } from '../types';
import { AIConfig } from './types';
import type { CommandWeightMap } from './weightedSelection';

/**
 * Lv6 達人 AI の Worker 通信プロトコル
 *
 * メインスレッドから Worker への送信、および Worker からメインスレッドへの応答の
 * メッセージ型を定義する。すべて structuredClone 可能（関数値を含まない）であること。
 */

/**
 * メイン → Worker のリクエストメッセージ
 */
export interface Lv6RequestMessage {
  kind: 'lv6/request';
  /** リクエスト識別子。複数リクエストの並列処理を可能にする */
  requestId: string;
  payload: {
    state: BattleState;
    playerId: 'player1' | 'player2';
    monster: Monster;
    opponentMonster: Monster;
    turnHistory: TurnResult[];
    aiConfig: AIConfig;
    /** 乱数 seed（関数を渡せないため数値で送る） */
    randomSeed: number;
    /**
     * ボス専用モディファイア（任意）。issue #264 ボス実装で活用。
     * シリアライズ可能なプレーンオブジェクト。
     */
    bossSpecificMods?: CommandWeightMap;
  };
}

/**
 * Worker → メイン の正常応答
 */
export interface Lv6ResponseMessage {
  kind: 'lv6/response';
  requestId: string;
  result: TurnCommands;
}

/**
 * Worker → メイン のエラー応答
 *
 * Worker 内で投げられた例外を構造化して返す。
 * Error オブジェクト自体は postMessage 可能だが、stack が省略されたり
 * 環境差があるためプレーンオブジェクト化して扱う。
 */
export interface Lv6ErrorMessage {
  kind: 'lv6/error';
  requestId: string;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
}

/**
 * Worker → メイン の応答メッセージのユニオン
 */
export type Lv6WorkerMessage = Lv6ResponseMessage | Lv6ErrorMessage;

/**
 * Lv6 Worker タイムアウト時のエラー
 */
export class Lv6TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'Lv6TimeoutError';
  }
}

/**
 * Lv6 Worker 内で発生したエラーの再現用
 */
export class Lv6WorkerError extends Error {
  constructor(message: string, public readonly originalName: string, public readonly originalStack?: string) {
    super(message);
    this.name = 'Lv6WorkerError';
  }
}
